import { action, observable, toJS } from 'mobx';
import { api } from '../config';

// Axios (used for loading static translation file)
import axios from 'axios';

let obx = observable({
  appType: 'standalone',

  /*-------------------------------------------
    Loading
  -------------------------------------------*/
  loadingCalls: [],
  loading: false,
  loadingClass: '',
  passwordInput:'',
  authResponse: null,

  /**
	 * startLoading - Sets loading state to true and adds the provided request to the array of current requests
   * @param {string} request - The name of the request being loaded
	 */
  startLoading: action(function(request) {
    obx.loading = true;
    obx.loadingCalls.push(request)
  }),

  /**
	 * finishLoading - Removes the provided request from the array of requests and sets the loading state to false if the request array is empty
   * @param {string} request - The name of the request being loaded
	 */
  finishLoading: action(function(request) {
    const requestIndex = obx.loadingCalls.indexOf(request);

    if (requestIndex >= 0) {
      obx.loadingCalls.splice(requestIndex, 1);
    } else {
      obx.loadingCalls.length = 0;
    }

    if (obx.loadingCalls.length === 0 || typeof request === 'undefined') {
      obx.loadingClass = 'leave';
      obx.loading = false;

      // Wait for loader to fade out before removing
      setTimeout(function(){
        obx.loadingClass = '';
        obx.loadingMessage = null;
      }, 250);
    }
  }),

  /*-------------------------------------------
    Error handling
  -------------------------------------------*/
  /**
	 * throwError - Determines whether to show a specific or general error message
   * @param {object} error
	 */
  throwError: action(function(error){
    obx.finishLoading();

    console.error(error);

    if (typeof error.response !== 'undefined') {
      // API errors will return an error object with a response
      // which means we have a message for them
      obx.showErrorMsg(error.response.data);
    } else {
      // If there’s no response with the error, it’s a server
      // connection error (i.e. axios can’t reach the URL)
      obx.showErrorMsg();
    }
  }),

  /**
	 * showErrorMsg - Shows a pop-up dialog with the appropriate error message for a provided error
   * @param {object} data - The error data
   * @param {string} data.code - The error code
	 */
  showErrorMsg: action(function(data){
    obx.dialogContent.heading = obx.translation.Errors.heading;
    obx.dialogContent.button = obx.translation.Errors.button;

    if (!data) {
      // Server error (API call returned no data)
      obx.dialogContent.body = obx.translation.Errors[1301].message;
    } else {
      // API call returned data
      if (typeof obx.translation.Errors[data.code] !== 'undefined') {
        // Error translation exists
        obx.dialogContent.body = obx.translation.Errors[data.code].message;
      } else {
        // Error translation does not exist
        console.warn('Error code not present in translation file. Falling back to API response message');
        obx.dialogContent.body = data.message;
      }
    }

    obx.dialog = true;

    // Redirect expired session
    if (data && data.code === '5002') {
      window.location.href = '/session-expired';
    }
  }),

  /*-------------------------------------------
    Translation
  -------------------------------------------*/
  locale: 'en',
  translation: null,
  translationLoaded: false,

  /**
	 * getTranslation - Loads the appropriate translation file
   * @param {string} locale
	 */
  getTranslation: action(function(locale){
    obx.translationLoaded = true;

    const timestamp = (new Date()).getTime();

    obx.startLoading('getTranslation');

    axios.get(`/json/${obx.locale}.json?t=${timestamp}`)
      .then(action(response => {
        obx.translation = response.data.content;
        obx.finishLoading('getTranslation');
      }))
      .catch(error => {
        obx.throwError(error);
      });
  }),

  /*-------------------------------------------
    Dialog box
  -------------------------------------------*/
  dialog: false,
  dialogContent: {},
  dialogOnCancel: null,
  dialogOnChange: null,
  dialogOnConfirm: null,
  dialogOnTimeout: null,
  dialogTimeout: null,
  dialogType: null,

  /**
	 * Show a pop-up dialog box with the provided content and options.
   * 
   * @function showDialog
   * @param {object} opts
   * @param {object} opts.content
   * @param {string} [opts.type]
   * @param {integer} [opts.timeout]
   * @param {array} [opts.options]
   * @param {function} [opts.onConfirm]
   * @param {function} [opts.onCancel]
   * @param {function} [opts.onTimeout]
   * @param {function} [opts.onChange]
	 */
  showDialog: action(opts => {
    if (opts.content) {
      obx.dialogContent = opts.content;
    }

    if (opts.type) {
      obx.dialogType = opts.type;
    }

    if (opts.timeout) {
      obx.dialogTimeout = opts.timeout;
    }

    if (opts.options) {
      obx.dialogOptions = opts.options;
    }

    if (opts.onConfirm) {
      obx.dialogOnConfirm = opts.onConfirm;
    }

    if (opts.onCancel) {
      obx.dialogOnCancel = opts.onCancel;
    }

    if (opts.onTimeout) {
      obx.dialogOnTimeout = opts.onTimeout;
    }

    if (opts.onChange) {
      obx.dialogOnChange = opts.onChange;
    }

    obx.dialog = true;
  }),

  /**
	 * closeDialog - Closes the pop-up dialog box
	 */
  closeDialog: action(() => {
    obx.dialog = false;
    obx.dialogContent = null;
    obx.dialogOnCancel = null;
    obx.dialogOnChange = null;
    obx.dialogOnConfirm = null;
    obx.dialogOnTimeout = null;
    obx.dialogTimeout = null;
    obx.dialogType = null;

    // Focus the main content element
    const main = document.querySelector('#main-content');

    if (main) {
      main.focus();
    }
  }),

  /*-------------------------------------------
    Drawer
  -------------------------------------------*/
  drawer: false,
  drawerContent: null,
  drawerTitle: null,
  drawerProps: null,
  drawerLeave: false,

  /**
   * openDrawer
   * @param {object} drawerContent
   */
  openDrawer: action((drawerContent, drawerTitle, drawerProps) => {
    obx.drawerContent = drawerContent;

    if (drawerTitle) {
      obx.drawerTitle = drawerTitle;
    }

    if (drawerProps) {
      obx.drawerProps = drawerProps;
    }

    obx.drawer = true;
  }),

  /**
   * closeDrawer
   */
  closeDrawer: action(() => {
    obx.drawerLeave = true;

    setTimeout(() => {
      obx.drawer = false;
      obx.drawerContent = null;
      obx.drawerTitle = null;
      obx.drawerLeave = false;
    }, 350);
  }),

  /*-------------------------------------------
    Toast message
  -------------------------------------------*/
  toast: null,
  toastType: null,
  toastTimeout: 5000,

  /**
   * Show a toast message.
   * 
   * @function showToast
   * @param {string} content
   * @param {string} type
   * @param {integer} timeout
   */
  showToast: action((content, type, timeout = 5000) => {
    obx.toast = content;
    obx.toastType = type;
    obx.toastTimeout = timeout;
  }),

  /**
   * Closes a toast message.
   * 
   * @function closeToast
   * @param {object} content
   */
  closeToast: action(() => {
    obx.toast = null;
    obx.toastType = null;
    obx.toastTimeout = 5000;
  }),

  /*-------------------------------------------
    Modal
  -------------------------------------------*/
  modal: false,
  modalLeave: false,
  modalContent: null,

  /**
   * showModal
   * @param {object} settings
   */
  showModal: action((content, heading) => {
    obx.modalContent = content;

    if (heading) {
      obx.modalHeading = heading;
    }
    
    obx.modal = true;
  }),

  /**
   * dismissModal
   */
  dismissModal: action(() => {
    obx.modalLeave = true;

    setTimeout(() => {
      obx.modal = false;
      obx.modalContent = null;
      obx.modalLeave = false;
    }, 250);
  }),

  /*-------------------------------------------
    Time zones
  -------------------------------------------*/
  timeZoneList: null,
  timeZone: null,
  timeZoneSid: null,

  authenticate: action((shareSid, callback) => {
    obx.startLoading('getAuthenticate');

    api.get(`/share/${shareSid}/password/${obx.passwordInput}/validate`)
      .then(response => {
        if (callback){
          callback(response.data);
        }
        obx.finishLoading('getAuthenticate');

      })
      .catch(error => {
        obx.throwError(error);
      })
  }),



  /**
   * getUserTimeZone - Uses the Intl API to determine the client machine’s time zone
   */
  getUserTimeZone: action(function(){
    obx.timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    obx.getServerTimeZones();
  }),

  /**
   * getServerTimeZones - Gets a list of time zones with SIDs from the server
   */
  getServerTimeZones: action(function(){
    obx.startLoading('getServerTimeZones');

    api.get('/timeZones')
      .then(response => {
        obx.timeZoneList = response.data.results;

        // Determine the SID of the client machine’s time zone
        const serverTimeZone = obx.timeZoneList.filter(zone => zone.name === obx.timeZone);

        if (serverTimeZone.length > 0) {
          obx.timeZoneSid = serverTimeZone[0].sid;
        }

        obx.finishLoading('getServerTimeZones');
      })
      .catch(error => {
        obx.throwError(error);
      });
  }),

  // Flag shared video viewer
  sharedViewer: false
});

export default obx;
